import { memo, useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { BaseLocationInfoWindowProps } from './types';
import { LocationHeader } from './components/LocationHeader';
import { StandardLocationView } from './StandardLocationView';
import { PotentialLocationView } from './PotentialLocationView';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { 
  selectActiveLocationDetails, 
  selectActiveLocationIsLoading,
  ReduxLocation
} from '@/store/slices/activeLocationSlice';
import { PotentialLocation, FirestoreLocation, LocationType } from '@/types/firestore';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { LocationComments } from './components/LocationComments';

import { Button } from '@/components/ui/button';
import { Star, BookmarkPlus, MessageCircle, Lock, Loader2 } from 'lucide-react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuCheckboxItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from '@/components/ui/checkbox';
import { RatingStars } from './components/RatingStars';
import { Textarea } from '@/components/ui/textarea';
import { GeoPoint, Timestamp } from 'firebase/firestore';
import { selectUser, selectIsAuthenticated } from '@/store/slices/authSlice';
import {
    selectAllMyMaps,
    addLocationToMap,
    removeLocationFromMap,
    createCustomMap,
    fetchMyMaps,
} from '@/store/slices/customMapsSlice';
import { 
    selectIsLocationVisited, 
    selectVisitedLocationRating,
    selectUserSpecificOperationLoading,
    selectVisitedLoading,
    toggleVisited,
    updateRating,
    updateVisitedLocation,
} from '@/store/slices/userSpecificSlice';
import { RootState } from '@/store';
import { updateLocation, selectLocationsLoading } from '@/store/slices/locationsSlice';
import { toast } from 'sonner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Internal component that renders the actual content, now consuming RTK state.
 * Updated to use PotentialLocationView for potential locations.
 * Layout changed to a two-column view: main content on left, action buttons on right.
 */
const LocationInfoWindowContent = memo(({
  location,
  onClose, 
  onUpdate, 
  onRemove 
}: BaseLocationInfoWindowProps) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const myMaps = useAppSelector(selectAllMyMaps);

  const activeLocationDetails = useAppSelector(selectActiveLocationDetails);
  const isLoading = useAppSelector(selectActiveLocationIsLoading);
  const locationsSliceLoading = useAppSelector(selectLocationsLoading);
  
  const isPotential = !location.id || location.id.startsWith('potential-');

  const displayLocation = isPotential ? location : activeLocationDetails;
  const displayRatingCount = isPotential ? location.ratingCount : activeLocationDetails?.ratingCount;
  const displayAverageRating = isPotential ? location.averageRating : activeLocationDetails?.averageRating;
  
  const currentDisplayLocationId = displayLocation?.id;

  const isVisited = useAppSelector(selectIsLocationVisited(currentDisplayLocationId));
  const currentRating = useAppSelector(selectVisitedLocationRating(currentDisplayLocationId)) ?? 0;
  const userVisitedLoading = useAppSelector(selectVisitedLoading);
  const userOpsLoading = useAppSelector(selectUserSpecificOperationLoading);

  const userVisitedLocation = useAppSelector((state: RootState) =>
    currentDisplayLocationId ? state.userSpecific.visitedLocations.entities[currentDisplayLocationId] : undefined
  );

  const [isCreateMapDialogOpen, setIsCreateMapDialogOpen] = useState(false);
  const [newMapNameInDialog, setNewMapNameInDialog] = useState('');
  const [publicCommentPopoverOpen, setPublicCommentPopoverOpen] = useState(false);
  const [privateCommentPopoverOpen, setPrivateCommentPopoverOpen] = useState(false);
  const [ratingPopoverOpen, setRatingPopoverOpen] = useState(false);
  const [publicNoteText, setPublicNoteText] = useState('');
  const [privateNoteText, setPrivateNoteText] = useState('');

  const [isUpdatingType, setIsUpdatingType] = useState(false);
  const [isCreatingMap, setIsCreatingMap] = useState(false);

  useEffect(() => {
    if (userVisitedLocation) {
      setPublicNoteText(userVisitedLocation.publicNote || '');
      setPrivateNoteText(userVisitedLocation.privateNote || '');
    } else {
      setPublicNoteText('');
      setPrivateNoteText('');
    }
  }, [userVisitedLocation]);

  const hasPublicNote = !!(userVisitedLocation?.publicNote && userVisitedLocation.publicNote.trim() !== '');
  const hasPrivateNote = !!(userVisitedLocation?.privateNote && userVisitedLocation.privateNote.trim() !== '');

  useEffect(() => {
    if (isAuthenticated && user?.id && !isPotential && currentDisplayLocationId) {
      if(myMaps.length === 0) dispatch(fetchMyMaps(user.id));
    }
  }, [isAuthenticated, user?.id, dispatch, myMaps.length, isPotential, currentDisplayLocationId]);

  const createFirestoreLocationStub = (loc: PotentialLocation | ReduxLocation): FirestoreLocation | null => {
    if (!loc?.id || !loc.name || !loc.address || !loc.locationType) {
        console.warn("Cannot create FirestoreLocation stub from incomplete location data", loc);
        return null;
    }

    let lastSightingTimestamp: Timestamp;
    if (loc.lastSightingDate) {
        if (typeof loc.lastSightingDate === 'number') {
            lastSightingTimestamp = Timestamp.fromMillis(loc.lastSightingDate);
        } else if (loc.lastSightingDate instanceof Timestamp) {
            lastSightingTimestamp = loc.lastSightingDate;
        } else {
            console.warn("Unexpected type for lastSightingDate, defaulting:", loc.lastSightingDate);
            lastSightingTimestamp = Timestamp.fromMillis(0);
        }
    } else {
        lastSightingTimestamp = Timestamp.fromMillis(0);
    }

    // Ensure ratingCount, averageRating, and drinkCount are numbers, default to 0 if not present or invalid
    const ratingCount = typeof loc.ratingCount === 'number' ? loc.ratingCount : 0;
    const averageRating = typeof loc.averageRating === 'number' ? loc.averageRating : 0;
    const drinkCount = typeof loc.drinkCount === 'number' ? loc.drinkCount : 0;

    return {
        id: loc.id,
        name: loc.name,
        address: loc.address,
        coordinates: loc.coordinates instanceof GeoPoint 
            ? loc.coordinates 
            : new GeoPoint(loc.coordinates.latitude, loc.coordinates.longitude),
        locationType: loc.locationType as LocationType,
        googlePlaceId: loc.googlePlaceId || undefined,
        lastSightingDate: lastSightingTimestamp,
        // Add missing properties required by FirestoreLocation type
        ratingCount: ratingCount,
        averageRating: averageRating,
        drinkCount: drinkCount,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
    };
};

  const handleToggleLocationInMap = useCallback((mapId: string) => {
    if (!currentDisplayLocationId) return;
    const isInMap = myMaps.find(m => m.id === mapId)?.locationIds.includes(currentDisplayLocationId) || false;
    if (isInMap) {
      dispatch(removeLocationFromMap({ mapId, locationId: currentDisplayLocationId }));
    } else {
      dispatch(addLocationToMap({ mapId, locationId: currentDisplayLocationId }));
    }
  }, [dispatch, currentDisplayLocationId, myMaps]);

  const handleCreateMapAndAddLocation = useCallback(async () => {
    if (newMapNameInDialog.trim() && user?.id && user?.displayName && currentDisplayLocationId) {
      setIsCreatingMap(true);
      try {
        const resultAction = await dispatch(createCustomMap({
          name: newMapNameInDialog.trim(),
          ownerId: user.id,
          ownerUsername: user.displayName
        }));
        if (createCustomMap.fulfilled.match(resultAction) && resultAction.payload) {
          const newMapId = resultAction.payload.id;
          dispatch(addLocationToMap({ mapId: newMapId, locationId: currentDisplayLocationId }));
        }
        setNewMapNameInDialog('');
        setIsCreateMapDialogOpen(false);
      } catch (error) {
        console.error("Failed to create map and add location:", error);
      } finally {
        setIsCreatingMap(false);
      }
    }
  }, [dispatch, newMapNameInDialog, user, currentDisplayLocationId]);

  const handleVisitedChange = useCallback(async () => {
    if (!displayLocation || !currentDisplayLocationId || isPotential) return;
    const firestoreLocationStub = createFirestoreLocationStub(displayLocation);
    if (!firestoreLocationStub) return;
    dispatch(toggleVisited({ location: firestoreLocationStub, currentVisitedState: !!isVisited }));
  }, [dispatch, displayLocation, currentDisplayLocationId, isVisited, isPotential]);

  const handleRatingChange = useCallback(async (rating: number) => {
    if (!currentDisplayLocationId || isPotential) return;
    dispatch(updateRating({ locationId: currentDisplayLocationId, rating }));
  }, [dispatch, currentDisplayLocationId, isPotential]);
  
  const handleLocationTypeChange = useCallback(async (newType: LocationType) => {
    if (!currentDisplayLocationId || isPotential) return;
    setIsUpdatingType(true);
    try {
      await dispatch(updateLocation({ id: currentDisplayLocationId, data: { locationType: newType } })).unwrap();
      toast.success(`Location type updated to ${newType}.`);
    } catch (error: any) { 
        toast.error(`Failed to update location type: ${error?.message || 'Unknown error'}`);
        console.error("Error updating type:", error);
    }
    finally { setIsUpdatingType(false); }
  }, [dispatch, currentDisplayLocationId, isPotential]);

  const handleSavePublicComment = useCallback(() => {
    if (!user?.id || !displayLocation || !currentDisplayLocationId || isPotential) return;
    if (!isVisited) {
        const firestoreLocationStub = createFirestoreLocationStub(displayLocation);
        if (firestoreLocationStub) {
            dispatch(toggleVisited({ location: firestoreLocationStub, currentVisitedState: false }));
        }
    }
    dispatch(updateVisitedLocation({ 
      locationId: currentDisplayLocationId,
      data: {
        publicNote: publicNoteText.trim() || null,
        privateNote: userVisitedLocation?.privateNote || null 
      }
    }));
    setPublicCommentPopoverOpen(false);
  }, [dispatch, user?.id, displayLocation, currentDisplayLocationId, isPotential, isVisited, publicNoteText, userVisitedLocation]);

  const handleSavePrivateComment = useCallback(() => {
    if (!user?.id || !displayLocation || !currentDisplayLocationId || isPotential) return;
    if (!isVisited) {
        const firestoreLocationStub = createFirestoreLocationStub(displayLocation);
        if (firestoreLocationStub) {
            dispatch(toggleVisited({ location: firestoreLocationStub, currentVisitedState: false }));
        }
    }
    dispatch(updateVisitedLocation({ 
      locationId: currentDisplayLocationId,
      data: {
        privateNote: privateNoteText.trim() || null,
        publicNote: userVisitedLocation?.publicNote || null
      }
    }));
    setPrivateCommentPopoverOpen(false);
  }, [dispatch, user?.id, displayLocation, currentDisplayLocationId, isPotential, isVisited, privateNoteText, userVisitedLocation]);

  const starIconFillClass = (isVisited && currentRating > 0) ? 'text-yellow-400' : 'text-muted-foreground';

  if (!displayLocation) {
    return (
      <Card className="w-[450px] max-h-[70vh] overflow-y-auto shadow-none border-0 box-border bg-card">
        <CardContent className="p-4 text-center text-muted-foreground">
          Location data not available.
        </CardContent>
      </Card>
    );
  }

  const iconSize = 24;
  const buttonClassNames = "p-1 h-10 w-10";

  return (
    <Card className="w-[450px] max-h-[70vh] shadow-none border-0 gap-0 pt-0 pb-3 box-border bg-card flex flex-row overflow-hidden">
      <div className="flex-grow flex flex-col min-w-0 overflow-y-auto">
        <LocationHeader
          location={displayLocation as PotentialLocation | ReduxLocation}
          isPotential={isPotential}
          averageRating={displayAverageRating ?? 0}
          ratingCount={displayRatingCount ?? 0}
          handleClose={onClose || (() => {})}
          locationType={displayLocation?.locationType}
          isUpdatingLocationType={isUpdatingType}
          onLocationTypeChange={handleLocationTypeChange}
          isLocationTypeDisabled={locationsSliceLoading === 'pending' || isPotential || isLoading}
          isVisited={isVisited}
        />
        <CardContent className="space-y-2 pt-2 px-3 box-border relative flex-grow">
          {isPotential ? (
            <PotentialLocationView 
              location={location}
              onClose={onClose!}
              onRemove={onRemove!} 
              onUpdate={onUpdate!} 
            />
          ) : (
            <StandardLocationView
              renderComments={() => <LocationComments />}
            />
          )}
          
          {!isPotential && isLoading && (
            <div className="absolute inset-0 bg-background/70 flex items-center justify-center z-10">
              <LoadingSpinner />
            </div>
          )}
        </CardContent>
      </div>

      {!isPotential && isAuthenticated && currentDisplayLocationId && (
        <div className="flex flex-col items-center space-y-2 p-3 pr-1 bg-card flex-shrink-0">
          <Popover open={ratingPopoverOpen} onOpenChange={setRatingPopoverOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant={isVisited && currentRating > 0 ? "default" : "outline"} className={`${buttonClassNames} relative`}>
                      <Star
                        size={iconSize}
                        style={{ minWidth: `${iconSize}px`, minHeight: `${iconSize}px` }}
                        className={starIconFillClass}
                      />
                      {isVisited && currentRating > 0 && (
                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-4/9 text-xs font-bold text-primary-foreground">
                          {currentRating}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {isVisited && currentRating > 0 ? `Rated ${currentRating}/5` : "Mark as visited to rate this location"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-auto p-3 bg-sidebar text-sidebar-foreground" align="end">
              <div className="space-y-2">
                <label htmlFor={`visited-${currentDisplayLocationId}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center">
                  <Checkbox 
                    id={`visited-${currentDisplayLocationId}`}
                    checked={!!isVisited}
                    onCheckedChange={handleVisitedChange}
                    disabled={userVisitedLoading === 'pending' || userOpsLoading.toggleVisited === 'pending' || userOpsLoading.updateRating === 'pending'}
                    className="mr-2"
                  />
                  Visited
                  {userOpsLoading.toggleVisited === 'pending' && (
                    <Loader2 className="ml-2 h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </label>
                <RatingStars
                  rating={currentRating}
                  isVisited={!!isVisited}
                  isDisabled={userVisitedLoading === 'pending' || userOpsLoading.updateRating === 'pending' || userOpsLoading.toggleVisited === 'pending' || !isVisited}
                  onRatingChange={handleRatingChange}
                />
              </div>
            </PopoverContent>
          </Popover>

          <Dialog open={isCreateMapDialogOpen} onOpenChange={setIsCreateMapDialogOpen}>
            <DropdownMenu modal={false}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button variant={currentDisplayLocationId && myMaps.some(map => map.locationIds.includes(currentDisplayLocationId)) ? "default" : "outline"} className={buttonClassNames}>
                        <BookmarkPlus
                          size={iconSize}
                          style={{ minWidth: `${iconSize}px`, minHeight: `${iconSize}px` }}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    {currentDisplayLocationId && myMaps.some(map => map.locationIds.includes(currentDisplayLocationId)) 
                      ? `Added to ${myMaps.filter(map => map.locationIds.includes(currentDisplayLocationId!)).length} Maps` 
                      : "Add to My Maps"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <DropdownMenuContent className="w-64 bg-sidebar text-sidebar-foreground" align="end">
                <div className="flex justify-between items-center pr-2">
                  <DropdownMenuLabel>Save to your maps</DropdownMenuLabel>
                  <Button variant="outline" size="sm" onClick={() => setIsCreateMapDialogOpen(true)} className="h-7 text-xs px-2">
                    New
                  </Button>
                </div>
                <DropdownMenuSeparator />
                <div className="max-h-48 overflow-y-auto pr-1">
                  {myMaps.map(map => {
                    const isInMap = map.locationIds.includes(currentDisplayLocationId!);
                    return (
                      <DropdownMenuCheckboxItem
                        key={map.id}
                        checked={isInMap}
                        onCheckedChange={() => handleToggleLocationInMap(map.id)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {map.name}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                  {myMaps.length === 0 && <DropdownMenuItem disabled>No maps created yet.</DropdownMenuItem>}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DialogContent className="sm:max-w-[425px] bg-sidebar text-sidebar-foreground">
              <DialogHeader>
                <DialogTitle>Create new map</DialogTitle>
                <DialogDescription>Enter a name for your new map.</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  id="newMapNameInDialog"
                  placeholder="My awesome map"
                  value={newMapNameInDialog}
                  onChange={(e) => setNewMapNameInDialog(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <DialogFooter>
                <Button type="button" onClick={() => setIsCreateMapDialogOpen(false)} variant="outline">Cancel</Button>
                <Button type="submit" onClick={handleCreateMapAndAddLocation} disabled={!newMapNameInDialog.trim() || !user?.id || isCreatingMap}>
                  {isCreatingMap ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating...</>
                  ) : (
                    "Create & Add"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Popover open={publicCommentPopoverOpen} onOpenChange={setPublicCommentPopoverOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant={hasPublicNote ? "default" : "outline"} className={buttonClassNames}>
                      <MessageCircle
                        size={iconSize}
                        style={{ minWidth: `${iconSize}px`, minHeight: `${iconSize}px` }}
                      />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {hasPublicNote ? userVisitedLocation?.publicNote : "Write a public comment"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-80 p-4 bg-sidebar text-sidebar-foreground" align="end">
              <h4 className="text-sm font-semibold mb-2">Public Comment</h4>
              {!user ? (
                <p className="text-sm text-muted-foreground italic">Sign in to add comments</p>
              ) : !isVisited ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground italic">Mark location as visited to add comments.</p>
                  <Button size="sm" onClick={() => setPublicCommentPopoverOpen(false)}>Close</Button>
                </div>
              ) : (
                <>
                  <Textarea 
                    value={publicNoteText} 
                    onChange={(e) => setPublicNoteText(e.target.value)}
                    placeholder="Add a public comment..."
                    className="min-h-[100px] text-sm resize-none"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => { setPublicNoteText(userVisitedLocation?.publicNote || ''); setPublicCommentPopoverOpen(false); }}>Cancel</Button>
                    <Button size="sm" onClick={handleSavePublicComment} disabled={userOpsLoading.updateVisitedLocation === 'pending' || userOpsLoading.toggleVisited === 'pending'}>Save</Button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>

          <Popover open={privateCommentPopoverOpen} onOpenChange={setPrivateCommentPopoverOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant={hasPrivateNote ? "default" : "outline"} className={buttonClassNames}>
                      <Lock
                        size={iconSize}
                        style={{ minWidth: `${iconSize}px`, minHeight: `${iconSize}px` }}
                      />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {hasPrivateNote ? userVisitedLocation?.privateNote : "Write a private note"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <PopoverContent className="w-80 p-4 bg-sidebar text-sidebar-foreground" align="end">
              <h4 className="text-sm font-semibold mb-2">Private Note</h4>
              {!user ? (
                <p className="text-sm text-muted-foreground italic">Sign in to add notes</p>
              ) : !isVisited ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground italic">Mark location as visited to add notes.</p>
                  <Button size="sm" onClick={() => setPrivateCommentPopoverOpen(false)}>Close</Button>
                </div>
              ) : (
                <>
                  <Textarea 
                    value={privateNoteText} 
                    onChange={(e) => setPrivateNoteText(e.target.value)}
                    placeholder="Add a private note..."
                    className="min-h-[100px] text-sm resize-none"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => { setPrivateNoteText(userVisitedLocation?.privateNote || ''); setPrivateCommentPopoverOpen(false); }}>Cancel</Button>
                    <Button size="sm" onClick={handleSavePrivateComment} disabled={userOpsLoading.updateVisitedLocation === 'pending' || userOpsLoading.toggleVisited === 'pending'}>Save</Button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>
        </div>
      )}
    </Card>
  );
});

LocationInfoWindowContent.displayName = 'LocationInfoWindowContent';

const LocationInfoWindow = memo(({ 
  location, 
  onClose, 
  onUpdate, 
  onRemove 
}: BaseLocationInfoWindowProps) => {
  return (
    <LocationInfoWindowContent 
      location={location}
      onClose={onClose} 
      onUpdate={onUpdate} 
      onRemove={onRemove} 
    />
  );
});

LocationInfoWindow.displayName = 'LocationInfoWindow';

export default LocationInfoWindow; 